<template>
  <div>
    <BaseDialog
        title="在庫調整"
        width="70%"
        :visible.sync="dialogVisible"
        :before-close="close"
        :show-only-close-button="false"
        :disable-ok-button="disableSaveButton"
        @ok="save"
        @cancel="close">
      <template slot="contents">
        <div class="dialog-contents">
          <template>
            <BaseTableDetail
                :table-set-list="_tableSetList1"
                :display-item="site"
                class="detail-table"
                :editable="componentEditable" />
            <el-button class="add-button"
                @click="addRow">
              行追加
            </el-button>
            <BaseTableList
                :table-set-list="_tableSetList2"
                :display-items="skuItems"
                :editable="componentEditable"
                validate-scope="tableList">
              <!-- オペレーションボタン -->
              <template
                  slot="operation"
                  slot-scope="{ row }">
                <BaseOperationTooltip
                    v-if="true"
                    :list="getTooltipList(row.row)"
                    placement="bottom-end" />
              </template>
            </BaseTableList>
          </template>
        </div>
      </template>
    </BaseDialog>
    <BaseDialog
        title="商品検索"
        append-to-body
        width="90%"
        :visible.sync="searchSkusDialogVisible"
        :show-footer="false">
      <template slot="contents">
        <SearchSkus
            @sku-selected="skuSelected" />
      </template>
    </BaseDialog>
  </div>
</template>

<style scoped>
.dialog-contents .detail-table ::v-deep th,
.dialog-contents .detail-table ::v-deep td {
  padding: 25px 15px;
  font-size: 14px;
  text-align: left;
}
.add-button {
  float: right;
  margin-right: 10px;
}
</style>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'

import common from '@/utils/common'
import message from '@/utils/message'
import * as define from '@/define'
import * as config from '@/config'
import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'
import BaseTableDetail from '@/components/molecules/BaseTableDetail'
import BaseDialog from '@/components/molecules/BaseDialog'
import SearchSkus from '@/components/pages/order/SearchSkus'
import BaseOperationTooltip from '@/components/molecules/BaseOperationTooltip'
import BaseTableList from '@/components/molecules/BaseTableList'

const COMPONENT_EDITABLE = Object.freeze({
  site: true, productId: false, productName: false, category: true, reason: true, note: true, qty: true
})
export default {
  name: 'StockAdjustmentDialog',
  components: {
    BaseTableDetail,
    BaseTableList,
    BaseDialog,
    BaseOperationTooltip,
    SearchSkus
  },
  props: {
    appendToBody: {
      type: Boolean,
      default: false
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  provide () {
    return {
      parentValidator: this.$validator
    }
  },
  data () {
    return {
      dialogVisible: false,
      searchSkusDialogVisible: false,
      site: { code: '' },
      skuItems: []
    }
  },
  computed: {
    define: () => define,
    ...mapState({
      sites: (state) => state.sites
    }),
    ...mapGetters({
      getServiceSiteConfig: 'getServiceSiteConfig'
    }),
    storePath () {
      return 'StockAdjustment'
    },
    componentEditable () {
      return COMPONENT_EDITABLE
    },
    _tableSetList1 () {
      return [
        {
          label: '対象在庫',
          properties: [
            { path: 'code', elementType: ELEMENT_TYPE.SELECT_BOX, type: 'String', codeName: 'sites', validateRule: 'required',
              selectCode: 'sites', validateAs: '対象在庫', editableId: 'site' }
          ]
        }
      ]
    },
    _tableSetList2 () {
      return [
        { label: 'SKU', path: 'productId', elementType: ELEMENT_TYPE.TEXT, editableId: 'productId', width: 200 },
        { label: '商品名', path: 'productName', elementType: ELEMENT_TYPE.TEXT, editableId: 'productName', width: 400 },
        { label: '区分', path: 'category', elementType: ELEMENT_TYPE.SELECT_BOX, selectCode: this.categoryArr, editableId: 'category',
          validateRule: 'required', validateAs: '区分', popoverErrorMsg: true },
        { label: '理由', path: 'reason', elementType: ELEMENT_TYPE.SELECT_BOX, selectCode: this.reasonArr, editableId: 'reason',
          validateRule: 'required', validateAs: '理由', popoverErrorMsg: true },
        { label: 'メモ', path: 'note', type: 'String', elementType: ELEMENT_TYPE.INPUT, editableId: 'note',
          validateRule: 'max:256', validateAs: 'メモ', popoverErrorMsg: true },
        { label: '数量', path: 'qty', type: 'Number', elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty', class: 'right',
          validateRule: 'required|min_value:1', validateAs: '数量', popoverErrorMsg: true, width: 100 },
        { type: 'Slot', path: 'operation', label: '', columnKey: 'operation', width: 60, class: 'right' }
      ]
    },
    disableSaveButton () {
      return this.skuItems.length < 1
    },
    categoryItems () {
      return this.skuItems.map(val => val.category)
    }
  },
  watch: {
    visible (val) {
      this.dialogVisible = val
    },
    dialogVisible (val) {
      this.$emit('update:visible', val)
    },
    categoryItems: {
      handler: function (val, oldVal) {
        // 一覧.区分が更新された時に、理由をクリアする
        if (val.length !== oldVal.length) {
          return
        }
        for (let i = 0; i < val.length; i++) {
          if (val[i] !== oldVal[i]) {
            this.skuItems[i].reason = ''
            return
          }
        }
      },
      deep: true
    }
  },
  created () {
    // 対象在庫リストが1つしかない場合は自動選択
    if (this.sites.length === 1) {
      this.site.code = this.sites[0].code
    }
  },
  methods: {
    /**
     * サービスサイト設定から区分を取得する
     */
    categoryArr () {
      let categoryArr = []
      let categorys = this.getServiceSiteConfig(null, 'd_stock_adjustment', 'category') || []

      Object.keys(categorys).forEach((key) => {
        categoryArr.push(
          {
            code: key,
            name: _.get(categorys[key], 'label', null)
          })
      })
      return categoryArr
    },
    /**
     * サービスサイト設定から理由を取得する
     */
    reasonArr (sku) {
      if (!sku.category) {
        return []
      }
      let reasonArr = []
      let reasons = this.getServiceSiteConfig(null, 'd_stock_adjustment', 'reason') || []

      Object.keys(reasons).forEach((key) => {
        if (_.get(reasons[key], 'v.0', null) === sku.category) {
          reasonArr.push(
            {
              code: key,
              name: _.get(reasons[key], 'label', null)
            })
        }
      })
      return reasonArr
    },
    /**
     * 明細に商品情報を設定
     * @param {*} row 商品情報
     */
    skuSelected (skuRow) {
      const code = _.get(skuRow, 'code')
      const name = _.get(skuRow, 'name')
      if (this.skuItems.findIndex(e => e.productId === code) < 0) {
        const newRow =
          {
            productId: code, // SKU
            productName: name, // 商品名
            category: '', // 区分
            reason: '', // 理由
            note: '', // メモ
            qty: 0 // 数量
          }
        this.skuItems.push(newRow)
      }
      // 商品検索ダイアログ画面クローズ
      this.searchSkusDialogVisible = false

    },
    /**
     * Tooltipの項目を返却する.
     * @param {Object} row sku情報
     * @return {Array}
     */
    getTooltipList (row) {
      return [
        {
          title: '削除',
          click: (item) => this.deleteRow(row.productId)
        }
      ]
    },
    /**
     * 行削除する.
     * @param {String} productId productId
     */
    deleteRow (productId) {
      const index = _.findIndex(this.skuItems, (val) => val.productId === productId)
      this.skuItems.splice(index, 1)
    },
    /**
     * 行追加する、商品検索ダイアログ画面を開く.
     */
    addRow () {
      this.searchSkusDialogVisible = true
    },
    /**
     * 保存処理.
     */
    async save () {
      // チェック処理
      if (!await this.$validator.validate()) {
        return
      }
      this.$confirm('保存しますか？', define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(async () => {
        try {
          this.$loadingService.show()
          // 更新処理APIを呼び出す
          await this.callApi()
          // 一覧画面データに更新したデータを反映する
          this.$emit('refreshList')
          this.$notify.success('保存しました。')
          // ダイアログ画面の情報をクリア
          this.clear()
        } catch (error) {
          const data = _.get(error, 'response.data')
          if (data == null) {
            common.addError(error, { msg: message.MSG_502() })
          } else {
            common.addError(error, { msg: message.MSG_554() })
          }
          this.$notify.error({ error: error })
          throw error
        } finally {
          this.$loadingService.close()
        }
      }).catch(() => {
        this.$notify.info('キャンセルしました。')
      })
    },
    /**
     * 更新 APIを呼び出す
     */
    async callApi () {
      let url = config.EC_STOCK_ADJUSTMENT
      let method = 'post'
      const skus = []
      this.skuItems.forEach((item) => {
        skus.push({
          skuCode: item.productId, // 商品ID(skuコード)
          qty: item.qty, // 移動数量
          category: item.category, // 区分
          reason: item.reason, // 理由コード
          note: item.note // メモ
        })
      })
      let data = {
        siteCode: this.site.code, // 対象在庫
        skus: skus // 商品
      }
      // 更新APIを呼び出す
      await common.apiAxios(url, { method, data })
    },
    /**
     * 画面情報をクリアする.
     * @param {String} productId productId
     */
    clear () {
      this.skuItems = []
      this.site.code = ''
      this.dialogVisible = false
    },
    /**
     * 画面をクローズする.
     */
    close () {
      if (this.skuItems.length > 0) {
        this.$confirm('入力を破棄しますか？', define.MESSAGE_TITLE, {
          confirmButtonText: 'はい',
          cancelButtonText: 'いいえ',
          type: 'warning'
        }).then(() => {
          this.clear()
          this.$validator.reset()
        }).catch(() => {})
      } else {
        this.clear()
      }
    }
  }
}
</script>
